import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { checkForUpdatesNow, onSwReady, type CheckForUpdatesResult } from '../app/registerSw';
import { APP_BUILD_TIME, APP_COMMIT } from '../buildInfo';
import { addProfile, nextProfileSortIndex } from '../features/profiles/profileMutations';
import { addRoutinesToProfile } from '../features/profiles/routineMutations';
import { downloadBackup } from '../features/settings/exportBackup';
import { parseImportedTree } from '../features/settings/importBackup';
import {
  parseRoutinePack,
  RoutinePackImportError,
  toRoutineDrafts,
  type RoutineDraft,
  type RoutinePackV1,
} from '../features/settings/importRoutines';
import { PinSetPrompt } from '../features/settings/PinSetPrompt';
import { bySortIndex } from '../lib/sortIndex';
import type { Profile } from '../lib/types';
import { uid } from '../lib/uid';
import { useDeviceStore } from '../store/device';
import { useSyncedStore } from '../store/synced';

// Pastel palette used to seed new profile cards. Round-robin by sortIndex
// so siblings tend to land on different colors.
const NEW_PROFILE_PALETTE = ['#fcd34d', '#93c5fd', '#86efac', '#fb7185', '#c4b5fd', '#fdba74'];

// Settings hub per plan/03 §Settings hub. Sync screen lives under M6;
// we stub it for now. Profile and routine editors arrive in M4 #2/#3.
export function SettingsHubPage() {
  const navigate = useNavigate();
  const tree = useSyncedStore((s) => s.tree);
  const setTree = useSyncedStore((s) => s.setTree);
  const profiles = (tree?.profiles ?? []).slice().sort(bySortIndex);
  const pin = useDeviceStore((s) => s.pin);
  const setPin = useDeviceStore((s) => s.setPin);
  const [pinDialog, setPinDialog] = useState(false);
  const [importPending, setImportPending] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [routinePackPending, setRoutinePackPending] = useState<RoutinePackV1 | null>(null);
  const [routinePackError, setRoutinePackError] = useState<string | null>(null);
  const [routinePackSuccess, setRoutinePackSuccess] = useState<string | null>(null);
  const [routinePackTargetProfile, setRoutinePackTargetProfile] = useState<string | null>(null);
  const routinePackInputRef = useRef<HTMLInputElement | null>(null);
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'checking' | CheckForUpdatesResult>(
    'idle',
  );
  // Hide the button until our SW has actually registered. `swReady`
  // stays false on dev (PWA plugin is dev-disabled), in tests, and on
  // any deploy where registration failed — a much tighter gate than
  // `'serviceWorker' in navigator`, which only asks if the browser
  // supports the API. `onSwReady` fires synchronously with the current
  // state on subscribe, so no flash of an unwanted button on mount.
  const [swReady, setSwReady] = useState(false);
  useEffect(() => onSwReady(setSwReady), []);

  const onCheckForUpdates = async () => {
    setUpdateStatus('checking');
    const r = await checkForUpdatesNow();
    setUpdateStatus(r);
  };

  const onExport = () => {
    if (!tree) return;
    downloadBackup(tree);
  };

  const onPickImportFile = () => {
    setImportError(null);
    fileInputRef.current?.click();
  };

  const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    try {
      const text = await file.text();
      // Parse + validate now so we can refuse before showing the confirm
      // dialog. Stash the raw text — final parse happens on confirm.
      parseImportedTree(text);
      setImportPending(text);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Tuonti epäonnistui.');
    }
  };

  const onConfirmImport = () => {
    if (!importPending) return;
    try {
      const next = parseImportedTree(importPending);
      setTree(next);
      setImportPending(null);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Tuonti epäonnistui.');
      setImportPending(null);
    }
  };

  const onPickRoutinePackFile = () => {
    setRoutinePackError(null);
    setRoutinePackSuccess(null);
    routinePackInputRef.current?.click();
  };

  const onRoutinePackFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const pack = parseRoutinePack(text);
      setRoutinePackPending(pack);
      setRoutinePackTargetProfile(null);
      setRoutinePackError(null);
      setRoutinePackSuccess(null);
    } catch (err) {
      setRoutinePackPending(null);
      setRoutinePackError(
        err instanceof RoutinePackImportError ? err.message : 'Tuonti epäonnistui.',
      );
    }
  };

  const onConfirmRoutinePackImport = () => {
    if (!tree || !routinePackPending || !routinePackTargetProfile) return;
    const drafts: RoutineDraft[] = toRoutineDrafts(routinePackPending);
    const targetProfile = tree.profiles.find((p) => p.id === routinePackTargetProfile);
    if (!targetProfile) return;
    setTree(addRoutinesToProfile(tree, routinePackTargetProfile, drafts));
    setRoutinePackSuccess(`Tuotu ${drafts.length} rutiinia profiilille ${targetProfile.name}.`);
    setRoutinePackPending(null);
    setRoutinePackTargetProfile(null);
  };

  const onCancelRoutinePackImport = () => {
    setRoutinePackPending(null);
    setRoutinePackTargetProfile(null);
  };

  const onAddProfile = () => {
    if (!tree) return;
    const sortIndex = nextProfileSortIndex(tree);
    const ts = new Date().toISOString();
    const newProfile: Profile = {
      id: uid(),
      name: 'Lapsi',
      avatarEmoji: '🧒',
      theme: { default: 'sky' },
      color: NEW_PROFILE_PALETTE[sortIndex % NEW_PROFILE_PALETTE.length] ?? '#fcd34d',
      sortIndex,
      routineIds: [],
      createdAt: ts,
      updatedAt: ts,
    };
    setTree(addProfile(tree, newProfile));
    navigate(`/settings/profiles/${newProfile.id}`);
  };

  return (
    <main className="settings">
      <Link to="/" className="back-button" aria-label="Takaisin profiilien valintaan">
        <span aria-hidden="true">←</span>
      </Link>
      <h1 className="settings-title">Asetukset</h1>

      <Section title="Profiilit">
        <ul className="settings-profile-list">
          {profiles.map((p) => (
            <li key={p.id}>
              <Link to={`/settings/profiles/${p.id}`} className="settings-profile-row">
                <span className="settings-profile-avatar" aria-hidden="true">
                  {p.avatarEmoji}
                </span>
                <span className="settings-profile-name">{p.name}</span>
                <span className="settings-profile-edit" aria-hidden="true">
                  ✎
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <button type="button" className="settings-button" onClick={onAddProfile}>
          + Lisää profiili
        </button>
      </Section>

      <Section title="Tehtävälistat">
        <p className="settings-hint">
          Muokkaa perheen tehtävälistoja — luo uusia, kopioi tehtäviä, näet mihin lapsiin ne on
          liitetty.
        </p>
        <Link to="/settings/routines" className="settings-button">
          Avaa tehtävälistat
        </Link>
      </Section>

      <Section title="Synkronointi">
        <p className="settings-hint">
          Yhdistä laite perheen tilille, niin rutiinit näkyvät jokaisella laitteella
          samanaikaisesti.
        </p>
        <Link to="/settings/sync" className="settings-button">
          Avaa synkronointi
        </Link>
      </Section>

      <Section title="Tuo rutiinit">
        <p className="settings-hint">
          Lataa rutiinipaketti JSON-tiedostosta valitsemasi profiilin alle. Ei korvaa olemassa
          olevia rutiineja — uudet lisätään listan loppuun.
        </p>
        <button type="button" className="settings-button" onClick={onPickRoutinePackFile}>
          Tuo rutiineja tiedostosta
        </button>
        <input
          ref={routinePackInputRef}
          type="file"
          accept=".json,application/json"
          onChange={onRoutinePackFile}
          aria-label="Valitse rutiinipakettitiedosto"
          hidden
        />
        {routinePackError && (
          <p className="settings-error" role="alert">
            {routinePackError}
          </p>
        )}
        {routinePackSuccess && (
          <p className="settings-hint" role="status">
            {routinePackSuccess}
          </p>
        )}
      </Section>

      <Section title="Varmuuskopio">
        <p className="settings-hint">
          Tallenna kaikki profiilit ja rutiinit tiedostoon, tai palauta aiempi varmuuskopio.
        </p>
        <div className="settings-button-row">
          <button type="button" className="settings-button" onClick={onExport}>
            Lataa varmuuskopio
          </button>
          <button type="button" className="settings-button" onClick={onPickImportFile}>
            Palauta varmuuskopiosta
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={onImportFile}
          aria-label="Valitse varmuuskopiotiedosto"
          hidden
        />
        {importError && (
          <p className="settings-error" role="alert">
            {importError}
          </p>
        )}
      </Section>

      <Section title="PIN-koodi">
        <p className="settings-hint">
          {pin === null
            ? 'PIN ei ole käytössä. Aseta PIN suojataksesi asetukset lapsilta.'
            : 'PIN on käytössä.'}
        </p>
        <button type="button" className="settings-button" onClick={() => setPinDialog(true)}>
          {pin === null ? 'Aseta PIN' : 'Vaihda PIN'}
        </button>
      </Section>

      <Section title="Tietoja">
        <p className="settings-hint">Aamusankari · Tehty rakkaudella 💛</p>
        <p className="settings-version">
          Versio <code>{APP_COMMIT}</code> · {formatBuildTime(APP_BUILD_TIME)}
        </p>
        {swReady && (
          <>
            <button
              type="button"
              className="settings-button"
              onClick={onCheckForUpdates}
              disabled={updateStatus === 'checking'}
            >
              Tarkista päivitykset
            </button>
            {updateStatus !== 'idle' && (
              <p className="settings-hint" role="status">
                {updateStatusMessage(updateStatus)}
              </p>
            )}
          </>
        )}
      </Section>

      {pinDialog && (
        <PinSetPrompt
          onSet={(value) => {
            setPin(value);
            setPinDialog(false);
          }}
          onCancel={() => setPinDialog(false)}
        />
      )}
      {routinePackPending && (
        <div className="confirm-modal" role="dialog" aria-label="Tuo rutiinit">
          <button
            type="button"
            className="confirm-backdrop"
            aria-label="Sulje"
            onClick={onCancelRoutinePackImport}
          />
          <div className="confirm-box">
            <h2>Tuo rutiinit</h2>
            <RoutinePackPreview pack={routinePackPending} />
            <h3 className="share-import-subtitle">Valitse profiili:</h3>
            <ul className="profile-grid">
              {profiles.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="profile-tile"
                    aria-pressed={routinePackTargetProfile === p.id}
                    onClick={() => setRoutinePackTargetProfile(p.id)}
                    aria-label={`Tuo profiilille: ${p.name}`}
                  >
                    <span aria-hidden="true">{p.avatarEmoji}</span>
                    <span>{p.name}</span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="confirm-actions">
              <button type="button" className="settings-button" onClick={onCancelRoutinePackImport}>
                Peruuta
              </button>
              <button
                type="button"
                className="settings-button"
                onClick={onConfirmRoutinePackImport}
                disabled={!routinePackTargetProfile}
              >
                Tuo
              </button>
            </div>
          </div>
        </div>
      )}
      {importPending && (
        <div className="confirm-modal" role="dialog" aria-label="Vahvista palautus">
          <button
            type="button"
            className="confirm-backdrop"
            aria-label="Sulje"
            onClick={() => setImportPending(null)}
          />
          <div className="confirm-box">
            <h2>Palauta varmuuskopio?</h2>
            <p>
              Kaikki nykyiset profiilit ja rutiinit korvataan tiedoston sisällöllä. Tätä ei voi
              perua.
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                className="settings-button"
                onClick={() => setImportPending(null)}
              >
                Peruuta
              </button>
              <button
                type="button"
                className="settings-button settings-button-danger"
                onClick={onConfirmImport}
              >
                Palauta
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function RoutinePackPreview({ pack }: { pack: RoutinePackV1 }) {
  const drafts = toRoutineDrafts(pack);
  return (
    <>
      <p>{drafts.length} rutiinia:</p>
      <ul className="share-import-tasks">
        {drafts.map((d, i) => (
          <li key={i}>
            <span aria-hidden="true">{d.emoji}</span> {d.name} — {d.tasks.length} tehtävää
          </li>
        ))}
      </ul>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="settings-section">
      <h2 className="settings-section-title">{title}</h2>
      {children}
    </section>
  );
}

// "2026-05-05T13:42:01.000Z" → "5.5.2026 13:42". Best effort: invalid input
// falls back to the raw string so this never explodes the settings screen.
function formatBuildTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function updateStatusMessage(status: 'checking' | CheckForUpdatesResult): string {
  switch (status) {
    case 'checking':
      return 'Tarkistetaan…';
    case 'updated':
      return 'Päivitys löytyi, ladataan…';
    case 'no-update':
      return 'Sovellus on jo uusin.';
    case 'offline':
      return 'Ei verkkoyhteyttä.';
    case 'unsupported':
      // The button is gated on `swReady`, so this branch is no longer
      // reachable from the UI. Kept for the type's sake; if a future
      // caller invokes checkForUpdatesNow without checking swReady
      // first, they'll get a non-empty string here rather than crash.
      return 'Päivityksiä ei voi tarkistaa tällä laitteella.';
  }
}
